import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './FindYourRightCar.css';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { globalFetchCarsData, GlobalCarVariant } from '../../redux/slices/globalCarsSlice';
import { 
  selectGlobalCarsLoading, 
  selectGlobalCarsError,
  selectGlobalCarsWithEmi 
} from '../../redux/selectors';
import OptimizedImage from '../OptimizedImage/OptimizedImage';

interface CarCardProps {
  id: string;
  image: string;
  name: string;
  downPayment: string;
  monthlyEmi?: string;
  variants?: GlobalCarVariant[];
  badge?: string;
}

/* ── Reference-Matching Compact Car Card ── */
const CarCard: React.FC<CarCardProps> = ({ id, image, name, downPayment, monthlyEmi }) => (
  <div className="compact-car-card">
    <Link to={`/car/${id}`} className="compact-car-image-link">
      <div className="compact-car-image-box">
        <OptimizedImage 
          src={image} 
          alt={name} 
          loading="lazy"
          className="compact-car-image-img"
          onError={() => console.warn(`Failed to load image for ${name}: ${image}`)}
          placeholder="/Website-Images/Cars/placeholder.svg"
        />
      </div>
    </Link>

    <div className="compact-car-info">
      <Link to={`/car/${id}`} className="compact-car-name-link">
        <h3 className="compact-car-name">{name}</h3>
      </Link>
      
      <div className="compact-car-pricing">
        <div className="compact-price-line">
          <span className="compact-price-label">Down Payment:</span>
          <span className="compact-price-val dp-highlight">{downPayment.replace('Down Payment: ', '')}</span>
        </div>
        {monthlyEmi && (
          <div className="compact-price-line emi-line">
            <span className="compact-price-label">EMI:</span>
            <span className="compact-price-val">{monthlyEmi}</span>
          </div>
        )}
      </div>

      <div className="compact-car-actions">
        <Link to={`/car/${id}`} className="compact-price-breakup-link">
          View Price Breakup
        </Link>
        <div className="compact-contact-icons">
          <a 
            href="tel:+919987828690" 
            className="compact-contact-icon call-icon" 
            title="Call ASW"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fa fa-phone"></i>
          </a>
          <a 
            href="https://wa.me/919987828417" 
            target="_blank" 
            rel="noopener noreferrer"
            className="compact-contact-icon whatsapp-icon" 
            title="WhatsApp ASW"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fa fa-whatsapp"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
);

/* 
── PREVIOUS DETAILED CARD (Preserved for future use) ──
const CarCardDetailed: React.FC<CarCardProps> = ({ id, image, name, downPayment, monthlyEmi, variants, badge }) => (
  <div className="car-card">
    <div className="todays-offers-badge">{badge || "Today's Offers"}</div>
    <Link to={`/car/${id}`} className="car-link">
      <div className="car-image">
        <OptimizedImage src={image} alt={name} loading="lazy" className="car-image-optimized" />
      </div>
      <div className="car-info">
        <h3 className="car-name">{name}</h3>
        ...
      </div>
    </Link>
  </div>
);
*/

const FindYourRightCar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  // Global state selectors
  const globalCarsWithEmi = useAppSelector(selectGlobalCarsWithEmi);
  const globalIsLoading = useAppSelector(selectGlobalCarsLoading);
  const globalError = useAppSelector(selectGlobalCarsError);

  // Search & Filter State
  const [carType, setCarType] = useState<'t-permit' | 'private'>('t-permit');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'hatchback' | 'sedan' | 'muv'>('all');
  const [selectedCity, setSelectedCity] = useState('Select City');
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const citySelectorRef = useRef<HTMLDivElement>(null);

  // Helper to categorize cars
  const getCarCategory = (name: string): 'hatchback' | 'sedan' | 'muv' => {
    const n = name.toLowerCase();
    if (n.includes('wagon') || n.includes('punch') || n.includes('tiago') || n.includes('i10') || n.includes('swift')) {
      return 'hatchback';
    }
    if (n.includes('aura') || n.includes('dzire') || n.includes('amaze') || n.includes('tigor') || n.includes('verna')) {
      return 'sedan';
    }
    return 'muv'; // Ertiga, Rumion, Innova Crysta
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (citySelectorRef.current && !citySelectorRef.current.contains(event.target as Node)) {
        setIsCityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch car data on component mount
  useEffect(() => {
    dispatch(globalFetchCarsData());
  }, [dispatch]);

  // Filter cars matching search query, category, and carType (t-permit vs private)
  const filteredCars = globalCarsWithEmi.filter((car) => {
    const currentCarType = car.carType || 't-permit';
    if (currentCarType !== carType) {
      return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const match = (
        car.name.toLowerCase().includes(q) ||
        car.variants.some((v) => v.name.toLowerCase().includes(q))
      );
      if (!match) return false;
    }
    if (selectedCategory !== 'all') {
      const cat = getCarCategory(car.name);
      if (cat !== selectedCategory) return false;
    }
    return true;
  });

  const matchingSuggestions = globalCarsWithEmi.filter((car) => {
    const currentCarType = car.carType || 't-permit';
    if (currentCarType !== carType) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      car.name.toLowerCase().includes(q) ||
      car.variants.some((v) => v.name.toLowerCase().includes(q))
    );
  }).slice(0, 6);

  return (
    <section className="find-your-car-section">
      {/* ── Hero Search Bar Card (Matching User Reference Image) ── */}
      <div className="hero-search-container">
        <div className="hero-search-card">
          <div className="hero-search-card-header">
            <h2 className="hero-search-card-title">
              Find Your Right Car
            </h2>
            <div className="hero-search-city-selector" ref={citySelectorRef}>
              <button
                type="button"
                className="hero-search-city-btn"
                onClick={() => setIsCityDropdownOpen((prev) => !prev)}
              >
                <span>{selectedCity}</span>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </button>
              {isCityDropdownOpen && (
                <div className="hero-city-dropdown">
                  {['Select City', 'Mumbai', 'Pune', 'Thane', 'Navi Mumbai', 'Nashik'].map((city) => (
                    <div
                      key={city}
                      className={`hero-city-item ${selectedCity === city ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedCity(city);
                        setIsCityDropdownOpen(false);
                      }}
                    >
                      {city}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hero-search-pill-wrapper" ref={searchWrapperRef}>
            {/* T-Permit vs Private Toggle Segment */}
            <div className="hero-search-toggle-group">
              <button
                type="button"
                className={`hero-search-toggle-btn ${carType === 't-permit' ? 'active' : ''}`}
                onClick={() => setCarType('t-permit')}
              >
                T-Permit
              </button>
              <button
                type="button"
                className={`hero-search-toggle-btn ${carType === 'private' ? 'active' : ''}`}
                onClick={() => setCarType('private')}
              >
                Private
              </button>
            </div>

            {/* Input field */}
            <input
              type="text"
              className="hero-search-input"
              placeholder="Type to select car name"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsDropdownOpen(true);
              }}
              onFocus={() => setIsDropdownOpen(true)}
            />

            {/* Clear Button */}
            {searchQuery && (
              <button
                type="button"
                className="hero-search-clear-btn"
                onClick={() => {
                  setSearchQuery('');
                  setIsDropdownOpen(false);
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}

            {/* Search Icon */}
            <button
              type="button"
              className="hero-search-icon-btn"
              onClick={() => setIsDropdownOpen(prev => !prev)}
              title="Search"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>

            {/* Autocomplete Dropdown */}
            {isDropdownOpen && matchingSuggestions.length > 0 && (
              <div className="hero-search-dropdown">
                {matchingSuggestions.map((car) => (
                  <div
                    key={car.id}
                    className="hero-search-dropdown-item"
                    onClick={() => {
                      setSearchQuery(car.name);
                      setIsDropdownOpen(false);
                      navigate(`/car/${car.id}`);
                    }}
                  >
                    <img src={car.image} alt={car.name} className="hero-search-item-img" />
                    <div className="hero-search-item-info">
                      <div className="hero-search-item-name">{car.name}</div>
                      <div className="hero-search-item-pricing">
                        <span>Down Payment: <strong>{car.downPayment}</strong></span>
                        {car.monthlyEmi && <span>EMI: <strong>{car.monthlyEmi}</strong></span>}
                      </div>
                    </div>
                    <span className="hero-search-item-badge">
                      {carType === 't-permit' ? 'T-Permit' : 'Private'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="section-container">
        <div className="section-subtitle">
          <p>
            {carType === 't-permit'
              ? '🚕 Commercial & Tourist T-Permit Vehicles with 100% On-Road Funding'
              : '🚗 Private Individual Registration Vehicles with Lowest Down Payment & Easy EMI'}
          </p>
          {globalError && (
            <div className="error-notice" style={{ 
              backgroundColor: '#fff3cd', 
              color: '#856404', 
              padding: '8px 16px', 
              borderRadius: '4px', 
              marginTop: '12px',
              fontSize: '14px'
            }}>
              ⚠️ {globalError}
            </div>
          )}
        </div>
        
        {/* ── Category Filter Header (Matching Reference Image) ── */}
        <div className="budget-cars-header">
          <div className="budget-cars-title-row">
            <span className="budget-accent-bar"></span>
            <h3 className="budget-cars-title">
              {carType === 't-permit' ? 'Popular T-Permit Commercial Cars' : 'Popular Private Cars'}
            </h3>
          </div>
          <div className="budget-category-pills">
            <button
              type="button"
              className={`budget-pill-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              All Cars
            </button>
            <button
              type="button"
              className={`budget-pill-btn ${selectedCategory === 'hatchback' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('hatchback')}
            >
              Hatchback
            </button>
            <button
              type="button"
              className={`budget-pill-btn ${selectedCategory === 'sedan' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('sedan')}
            >
              Sedan
            </button>
            <button
              type="button"
              className={`budget-pill-btn ${selectedCategory === 'muv' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('muv')}
            >
              MUV / 7-Seater
            </button>
          </div>
        </div>

        {globalIsLoading ? (
          <div className="loading-state" style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            color: '#64748b'
          }}>
            <div className="loading-spinner" style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '3px solid #e2e8f0',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '16px'
            }}></div>
            <p>Loading latest car offers from global state...</p>
          </div>
        ) : filteredCars.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 20px', color: '#64748b' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>🚗</div>
            <h3>No cars found matching "{searchQuery}"</h3>
            <p style={{ marginTop: '8px', fontSize: '14px' }}>Try clearing the search query or selecting another category.</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              style={{
                marginTop: '16px',
                padding: '8px 20px',
                background: '#00796B',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Show All Cars
            </button>
          </div>
        ) : (
          <div className="car-grid compact-car-grid">
            {filteredCars.map((car) => (
              <CarCard 
                key={car.id} 
                id={car.id}
                image={car.image} 
                name={car.name} 
                downPayment={car.downPayment}
                monthlyEmi={car.monthlyEmi}
                variants={car.variants}
                badge={carType === 't-permit' ? 'T-Permit Offer' : 'Private Car Offer'}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FindYourRightCar;